import { useContext, useMemo, useState } from 'react';

import {
  Box,
  alpha,
  Stack,
  lighten,
  Divider,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/Context/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { ThemeContext } from 'src/theme/ThemeProvider';

import HeaderButtons from './Buttons';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import HomeIcon from '@mui/icons-material/Home';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LanguageIcon from '@mui/icons-material/Language';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelectorPopup from 'src/components/LanguageSelector/LanguageSelectorPopup';
import NovaChatPanel from './NovaChatPanel';
import { isModernDashboardEnabled } from 'src/Common/navigation/getPostLoginHomePath';
import {
  noteDeviceSettingSaved,
  useManagedDeviceSetting,
  WebDeviceSetting
} from 'src/Common/settings/deviceSettings';
import ManagedSettingLock, { useManagedSettingTitle } from 'src/Common/settings/ManagedSettingLock';

const HeaderWrapper = styled(Box)(
  ({ theme }) => {
    // Immersive ("Full Gradient"): the header is FROSTED GLASS, not fully transparent.
    // A transparent bar lets scrolling content read straight through it, which looks
    // like the page is painted on top of the header — the blur + slight tint keeps the
    // single-canvas feel while pushing content visually behind.
    const immersive = Boolean((theme as any).modernImmersive);
    const headerBg = immersive
      ? alpha(theme.header.background, 0.55)
      : alpha(theme.header.background, 0.95);
    return `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${headerBg};
        backdrop-filter: ${immersive ? 'blur(18px) saturate(150%)' : 'blur(8px)'};
        transition: background-color 0.3s ease, color 0.3s ease;
        position: fixed;
        justify-content: space-between;
        width: 100%;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${theme.sidebar.width};
            width: auto;
        }
        @media (max-width: ${theme.breakpoints.values.sm}px) {
            padding: ${theme.spacing(0, 1)};
        }
        ${
          // Full Modern: controls/labels must read light on the dark canvas (the classic
          // primary colour would be unreadable). Immersive adds no surface of its own —
          // only a hairline so the header edge is still perceptible.
          (theme as any).isModern
            ? `
        ${
          immersive
            ? `background-image: none; box-shadow: none; border-bottom: 1px solid ${
                (theme as any).sidebar.dividerBg
              };`
            : 'background-image: linear-gradient(90deg, rgba(124,58,237,.38) 0%, rgba(79,70,229,.18) 48%, rgba(15,23,42,0) 100%); border-bottom: 1px solid rgba(255,255,255,.08);'
        }
        .MuiIconButton-root { color: ${theme.header.textColor}; border-radius: 12px; }
        .MuiIconButton-root:hover {
          color: ${(theme as any).sidebar.menuItemHoverColor || '#ffffff'};
          background-color: ${(theme as any).sidebar.menuItemHoverBg || 'rgba(255,255,255,.10)'};
        }
        .MuiTypography-root { color: ${theme.header.textColor}; }
        .MuiAvatar-root { box-shadow: 0 0 0 2px ${(theme as any).sidebar.dividerBg}; }
        `
            : ''
        }
`;
  }
);

function Header() {
  const { t } = useTranslation();
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const setThemeName = useContext(ThemeContext);
  const isDark = theme.palette.mode === 'dark';
  const nextTheme = useMemo(() => (isDark ? 'PureLightTheme' : 'DarkTheme'), [isDark]);
  // Light / dark may be managed centrally (Developer Settings → device.web.theme_mode).
  const themeManaged = useManagedDeviceSetting(WebDeviceSetting.themeMode);
  const managedTitle = useManagedSettingTitle();
  const toggleTheme = () => {
    if (themeManaged.locked) return;
    setThemeName(nextTheme);
    noteDeviceSettingSaved();
  };
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  // Recomputed each render (cheap sessionStorage read) so toggling the setting
  // takes effect without a reload. Only shown when the company opted in.
  const showModernHome = isModernDashboardEnabled();

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileLanguage = () => {
    handleMobileMenuClose();
    setIsLanguagePopupOpen(true);
  };

  const handleMobileChat = () => {
    handleMobileMenuClose();
    setIsChatOpen(true);
  };

  const handleMobileTheme = () => {
    handleMobileMenuClose();
    toggleTheme();
  };

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
    >
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="center"
        spacing={2}
      >
        <HeaderMenu />
      </Stack>

      <Box display="flex" alignItems="center" gap={0.5}>
        {/* Desktop: full icon row */}
        {/* Home → Modern Dashboard. Kept outside the desktop-only row so phones get a
            direct button instead of it hiding inside the "More" menu. */}
        {showModernHome && (
          <Tooltip arrow title="Home — Modern Dashboard">
            <IconButton color="primary" component={NavLink} to="/modern-dashboard">
              <HomeIcon />
            </IconButton>
          </Tooltip>
        )}

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          <Tooltip arrow title={t('header.supportTickets')}>
            <IconButton color="primary" component={NavLink} to="/support/tickets">
              <SupportAgentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('language.selectLanguage')}>
            <IconButton color="primary" onClick={() => setIsLanguagePopupOpen(true)}>
              <LanguageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t('header.setup')}>
            <IconButton color="primary" component={NavLink} to="/management/setup/tabs">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title="Chat">
            <IconButton color="primary" onClick={() => setIsChatOpen(true)}>
              <ChatBubbleOutlineIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            arrow
            title={
              themeManaged.locked
                ? managedTitle(themeManaged)
                : isDark ? t('header.switchToLightMode') : t('header.switchToDarkMode')
            }
          >
            {/* span: a disabled button fires no events for the tooltip */}
            <span>
              <IconButton color="primary" onClick={toggleTheme} disabled={themeManaged.locked}>
                {isDark ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <ManagedSettingLock managed={themeManaged} sx={{ ml: -0.75 }} />
          <HeaderButtons />
        </Box>

        {/* Mobile: collapsed "More" menu */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <Tooltip arrow title="More options">
            <IconButton color="primary" onClick={handleMobileMenuOpen} size="small">
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
          >
            <MenuItem component={NavLink} to="/support/tickets" onClick={handleMobileMenuClose}>
              <ListItemIcon><SupportAgentIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('header.supportTickets')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMobileLanguage}>
              <ListItemIcon><LanguageIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('language.selectLanguage')}</ListItemText>
            </MenuItem>
            <MenuItem component={NavLink} to="/management/setup/tabs" onClick={handleMobileMenuClose}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('header.setup')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMobileChat}>
              <ListItemIcon><ChatBubbleOutlineIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Chat</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleMobileTheme} disabled={themeManaged.locked}>
              <ListItemIcon>
                {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>
                {isDark ? t('header.switchToLightMode') : t('header.switchToDarkMode')}
              </ListItemText>
              <ManagedSettingLock managed={themeManaged} sx={{ ml: 1 }} />
            </MenuItem>
          </Menu>
        </Box>

        <HeaderUserbox />

        {/* Hamburger: visible on < lg */}
        <Box
          component="span"
          sx={{ display: { lg: 'none', xs: 'inline-block' } }}
        >
          <Tooltip arrow title={t('header.toggleMenu')}>
            <IconButton color="primary" onClick={toggleSidebar} size="small">
              {!sidebarToggle ? (
                <MenuTwoToneIcon fontSize="small" />
              ) : (
                <CloseTwoToneIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <LanguageSelectorPopup
        open={isLanguagePopupOpen}
        onClose={() => setIsLanguagePopupOpen(false)}
      />
      <NovaChatPanel open={isChatOpen} onClose={() => setIsChatOpen(false)} />
    </HeaderWrapper>
  );
}

export default Header;
